import React, { useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { earningsApi } from '../../api/earnings';
import { formatNgn } from '../../utils/format';

interface Props {
  available: number;
  /** Canonical minimum withdrawable amount (from the earnings summary). */
  minPayout: number;
  feePercent: number;
  feeCap: number;
  onClose: () => void;
  onSubmitted: () => void;
}

export const RequestPayoutModal: React.FC<Props> = ({
  available,
  minPayout,
  feePercent,
  feeCap,
  onClose,
  onSubmitted,
}) => {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  // Fall back to ₦1,000 if the summary somehow didn't include it.
  const min = minPayout > 0 ? minPayout : 1000;
  const amt = parseFloat(amount);
  const validAmt = !Number.isNaN(amt) && amt > 0;
  const fee = validAmt
    ? Math.min(Math.round((amt * feePercent) / 100), feeCap || Infinity)
    : 0;
  const net = validAmt ? amt - fee : 0;

  // Two gating checks: at/above the minimum, and not over the wallet balance.
  const belowMin = validAmt && amt < min;
  const exceedsBalance = validAmt && amt > available;
  const canSubmit = validAmt && !belowMin && !exceedsBalance && !busy;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      setBusy(true);
      await earningsApi.requestPayout(amt);
      toast.success('Payout requested. PayIQ will process it shortly.');
      onSubmitted();
    } catch (err) {
      toast.error(err, 'Failed to request payout.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Request payout">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            background: 'var(--blue-100)',
            color: 'var(--blue-900)',
            padding: 14,
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
          }}
        >
          Available balance: <strong>{formatNgn(available)}</strong>
          <br />
          Minimum withdrawal: <strong>{formatNgn(min)}</strong>
          <br />
          Funds land in your registered payout bank account, typically within
          one business day after PayIQ processes the request.
        </div>
        <Input
          label="Amount (NGN)"
          type="number"
          min={min}
          max={available}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`Min ${formatNgn(min)}`}
          error={
            belowMin
              ? `Minimum withdrawal is ${formatNgn(min)}.`
              : exceedsBalance
                ? `Amount exceeds your available balance (${formatNgn(available)}).`
                : undefined
          }
        />
        <div style={{ display: 'flex', gap: 8 }}>
          {[0.25, 0.5, 1].map((mult) => (
            <button
              key={mult}
              type="button"
              onClick={() =>
                setAmount(String(Math.floor(available * mult * 100) / 100))
              }
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-card)',
                color: 'var(--text-secondary)',
              }}
            >
              {mult === 1 ? 'All' : `${mult * 100}%`}
            </button>
          ))}
        </div>

        {canSubmit ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-muted)',
              fontSize: 13,
            }}
          >
            <Row label="Payout fee" value={fee > 0 ? `−${formatNgn(fee)}` : 'Free'} />
            <div style={{ height: 1, background: 'var(--border)' }} />
            <Row label="You receive" value={formatNgn(net)} strong />
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {feePercent}% fee{feeCap ? `, capped at ${formatNgn(feeCap)}` : ''}.
            </div>
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 8,
          }}
        >
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={busy} disabled={!canSubmit}>
            Request payout
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span
      style={{
        fontWeight: strong ? 700 : 500,
        color: 'var(--text-primary)',
      }}
    >
      {value}
    </span>
  </div>
);
